import * as T from "three";
const axes = ["x", "y", "z"];
const identity = new T.Matrix4();

/** Shared immutable support boxes: every vertex is included for each influencing bone. */
export function skinInfluenceBounds(geometry, boneCount) {
    const { position, skinIndex, skinWeight } = geometry.attributes;
    if (!position || !skinIndex || !skinWeight || position.itemSize !== 3
        || skinIndex.itemSize !== 4 || skinWeight.itemSize !== 4
        || skinIndex.count !== position.count || skinWeight.count !== position.count
        || !Number.isInteger(boneCount) || boneCount < 1 || boneCount > 256) throw new Error("Invalid skin bounds layout");
    const boxes = new Array(boneCount), point = new T.Vector3();
    let minimumSum = Infinity, maximumSum = 0;
    for (let vertex = 0; vertex < position.count; vertex++) {
        point.fromBufferAttribute(position, vertex);
        if (!Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(point.z)) throw new Error("Invalid skin bounds position");
        let sum = 0;
        for (let influence = 0; influence < 4; influence++) {
            const weight = skinWeight.getComponent(vertex, influence), bone = skinIndex.getComponent(vertex, influence);
            if (!Number.isFinite(weight) || weight < 0 || !Number.isInteger(bone) || bone < 0 || bone >= boneCount)
                throw new Error("Invalid skin bounds influence");
            sum += weight;
            if (weight > 0) (boxes[bone] ??= new T.Box3()).expandByPoint(point);
        }
        if (!Number.isFinite(sum) || sum <= 0) throw new Error("Empty skin bounds weights");
        minimumSum = Math.min(minimumSum, sum); maximumSum = Math.max(maximumSum, sum);
    }
    return { bones: boxes.flatMap((box, bone) => box ? [{ bone,
        cx: (box.min.x + box.max.x) / 2, cy: (box.min.y + box.max.y) / 2, cz: (box.min.z + box.max.z) / 2,
        rx: (box.max.x - box.min.x) / 2, ry: (box.max.y - box.min.y) / 2, rz: (box.max.z - box.min.z) / 2 }] : []), minimumSum, maximumSum };
}

/** Entity-owned bounds, recomputed from the final posed/bone-merged world matrices. */
export class AnimatedBounds {
    constructor(mesh, influences) {
        this.mesh = mesh; this.influences = influences;
        this.palette = new T.Matrix4(); this.transform = new T.Matrix4();
        mesh.boundingBox = new T.Box3(); mesh.boundingSphere = new T.Sphere();
    }
    update() {
        const { mesh, influences } = this, result = mesh.boundingBox;
        result.makeEmpty();
        const identityBind = mesh.bindMatrix.equals(identity);
        let magnitude = 1;
        for (const { bone, cx, cy, cz, rx, ry, rz } of influences.bones) {
            this.palette.multiplyMatrices(mesh.skeleton.bones[bone].matrixWorld, mesh.skeleton.boneInverses[bone]);
            // Skeleton.update uploads a Float32 palette; use the same rounding here.
            for (let i = 0; i < 16; i++) {
                this.palette.elements[i] = Math.fround(this.palette.elements[i]);
            }
            const palette = this.palette.elements;
            magnitude = Math.max(magnitude, Math.abs(palette[12]), Math.abs(palette[13]), Math.abs(palette[14]));
            this.transform.multiplyMatrices(mesh.bindMatrixInverse, this.palette);
            if (!identityBind) this.transform.multiply(mesh.bindMatrix);
            // Affine interval transform: equivalent to all eight AABB corners,
            // without per-corner vector operations or homogeneous division.
            const e = this.transform.elements;
            const x = e[0] * cx + e[4] * cy + e[8] * cz + e[12],
                y = e[1] * cx + e[5] * cy + e[9] * cz + e[13],
                z = e[2] * cx + e[6] * cy + e[10] * cz + e[14];
            const dx = Math.abs(e[0]) * rx + Math.abs(e[4]) * ry + Math.abs(e[8]) * rz,
                dy = Math.abs(e[1]) * rx + Math.abs(e[5]) * ry + Math.abs(e[9]) * rz,
                dz = Math.abs(e[2]) * rx + Math.abs(e[6]) * ry + Math.abs(e[10]) * rz;
            result.min.set(Math.min(result.min.x, x - dx), Math.min(result.min.y, y - dy), Math.min(result.min.z, z - dz));
            result.max.set(Math.max(result.max.x, x + dx), Math.max(result.max.y, y + dy), Math.max(result.max.z, z + dz));
        }
        if (!result.isEmpty()) {
            // Positive skin weights form a convex combination of these support boxes.
            // Float32 weights may sum just above/below one; bound that scale too.
            for (const axis of axes) {
                const a = result.min[axis], b = result.max[axis];
                result.min[axis] = Math.min(a * influences.minimumSum, a * influences.maximumSum,
                    b * influences.minimumSum, b * influences.maximumSum);
                result.max[axis] = Math.max(a * influences.minimumSum, a * influences.maximumSum,
                    b * influences.minimumSum, b * influences.maximumSum);
                magnitude = Math.max(magnitude, Math.abs(result.min[axis]), Math.abs(result.max[axis]));
            }
            // Allow shader/uniform Float32 arithmetic at large world coordinates.
            result.expandByScalar(magnitude * 0.00001);
        }
        result.getBoundingSphere(mesh.boundingSphere);
    }
}
